import { AxiosError } from 'axios';
import { apiService } from '@shared/services/apiServices';
import { QMS_CATEGORY_ERROR_MESSAGES } from '../errors/errorMessages';
import type { QmsCategory } from '../types/qmsCategoryTypes';
import qmsCategoryEndpoints from './qmsCategoryEndpoints';

export interface GetQmsCategoryResult {
  success: boolean;
  data?: QmsCategory;
  error?: string;
  notFound?: boolean;
  forbidden?: boolean;
}

const isInvalidIdError = (errorData: unknown): boolean => {
  if (errorData && typeof errorData === 'object') {
    const message = (errorData as { message?: unknown }).message;
    return typeof message === 'string' && message === 'Invalid UUID format';
  }
  return false;
};

const extractErrorMessage = (errorData: unknown, status?: number): string => {
  if (status === 401 || status === 403) {
    return QMS_CATEGORY_ERROR_MESSAGES.UNAUTHORIZED;
  }
  if (status === 500) {
    return QMS_CATEGORY_ERROR_MESSAGES.INTERNAL_SERVER_ERROR;
  }
  if (errorData && typeof errorData === 'object') {
    const obj = errorData as { message?: string; error?: string };
    if (obj.message) {
      return obj.message;
    }
    if (obj.error) {
      return obj.error;
    }
  }
  if (typeof errorData === 'string' && errorData.trim()) {
    return errorData;
  }
  return QMS_CATEGORY_ERROR_MESSAGES.FETCH_CATEGORY_ERROR;
};

const getCategory = async (
  categoryId: string
): Promise<GetQmsCategoryResult> => {
  const trimmedId = categoryId?.trim();
  if (!trimmedId) {
    return {
      success: false,
      error: QMS_CATEGORY_ERROR_MESSAGES.FETCH_CATEGORY_ERROR,
    };
  }

  try {
    const result = await apiService.getAPI(
      `${qmsCategoryEndpoints.CATEGORIES}/${trimmedId}`,
      { skipGlobalErrorRedirect: true }
    );

    if (!result.error) {
      const payload = (result.data ?? {}) as {
        category?: QmsCategory;
        data?: QmsCategory;
      } & Partial<QmsCategory>;
      const category = (payload.category ??
        payload.data ??
        payload) as QmsCategory;
      return { success: true, data: category };
    }

    if (result.isNetworkError) {
      return {
        success: false,
        error: QMS_CATEGORY_ERROR_MESSAGES.NETWORK_ERROR,
      };
    }

    if (result.status === 403) {
      return { success: false, forbidden: true };
    }

    if (result.status === 404 || isInvalidIdError(result.data)) {
      return { success: false, notFound: true };
    }

    return {
      success: false,
      error: extractErrorMessage(result.data, result.status),
    };
  } catch (error) {
    if (error instanceof AxiosError) {
      const status = error.response?.status;
      const errorData = error.response?.data;
      const isNetworkError = !error.response && !!error.request;

      if (isNetworkError) {
        return {
          success: false,
          error: QMS_CATEGORY_ERROR_MESSAGES.NETWORK_ERROR,
        };
      }

      if (status === 404 || isInvalidIdError(errorData)) {
        return { success: false, notFound: true };
      }

      return {
        success: false,
        error: extractErrorMessage(errorData, status),
      };
    }

    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : QMS_CATEGORY_ERROR_MESSAGES.UNEXPECTED_ERROR,
    };
  }
};

export const getQmsCategoryApiService = { getCategory };
